import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Pages
import LoginPage from './pages/LoginPage';
import HomePage from './pages/HomePage';
import SuperAdminPage from './pages/SuperAdminPage';
import DashboardPage from './pages/DashboardPage';
import CrearGuiaPage from './pages/CrearGuiaPage';
import MisGuiasPage from './pages/MisGuiasPage';
import AprobarGuiasPage from './pages/AprobarGuiasPage';
import PuntoSalidaPage from './pages/PuntoSalidaPage';
import EscanerQRPage from './pages/EscanerQRPage';
import DocumentosPage from './pages/DocumentosPage';
import DocumentoDetallePage from './pages/DocumentoDetallePage';
import ReportesGuiasPage from './pages/ReportesGuiasPage';

// Alarmas Module
import AlarmasPanelPage from './pages/alarmas/AlarmasPanelPage';
import AlarmasAdminPage from './pages/alarmas/AlarmasAdminPage';
import AlarmasHistorialPage from './pages/alarmas/AlarmasHistorialPage';
import AlarmasRegistroPublicoPage from './pages/alarmas/AlarmasRegistroPublicoPage';
import AlarmasManualPage from './pages/alarmas/AlarmasManualPage';
import CodelcoDashboardPage from './pages/codelco/CodelcoDashboardPage';
import CodelcoVehiculosPage from './pages/codelco/CodelcoVehiculosPage';
import CodelcoConfigPage from './pages/codelco/CodelcoConfigPage';
import CodelcoBitacoraPage from './pages/codelco/CodelcoBitacoraPage';
import CodelcoNotificacionesPage from './pages/codelco/CodelcoNotificacionesPage';
import CodelcoMapeoPage from './pages/codelco/CodelcoMapeoPage';
import CodelcoPerfilesPage from './pages/codelco/CodelcoPerfilesPage';

import CargaMasivaPage from './pages/CargaMasivaPage';
import ConfiguracionPage from './pages/ConfiguracionPage';
import ConfiguracionGuiasPage from './pages/ConfiguracionGuiasPage';
import HistorialPage from './pages/HistorialPage';
import UsuariosPage from './pages/UsuariosPage';
import GestionUsuariosPage from './pages/GestionUsuariosPage';

// Ordenes de Servicio Module Pages
import OrdenesDashboardPage from './pages/ordenes/OrdenesDashboardPage';
import OrdenesClientesPage from './pages/ordenes/OrdenesClientesPage';
import ClienteDetallePage from './pages/ordenes/ClienteDetallePage';
import OrdenesListPage from './pages/ordenes/OrdenesListPage';
import OrdenFormPage from './pages/ordenes/OrdenFormPage';
import OrdenDetallePage from './pages/ordenes/OrdenDetallePage';
import OrdenesInventarioPage from './pages/ordenes/OrdenesInventarioPage';
import OrdenesVehiculosPage from './pages/ordenes/OrdenesVehiculosPage';
import VehiculoDetallePage from './pages/ordenes/VehiculoDetallePage';
import OrdenesDispositivosPage from './pages/ordenes/OrdenesDispositivosPage';
import DispositivosInstaladosPage from './pages/ordenes/DispositivosInstaladosPage';
import OrdenesConfigPage from './pages/ordenes/OrdenesConfigPage';
import OrdenesCalendarioPage from './pages/ordenes/OrdenesCalendarioPage';
import OrdenesReportesPage from './pages/ordenes/OrdenesReportesPage';
import ReportesCustomPage from './pages/ordenes/ReportesCustomPage';
import CertificadosPage from './pages/ordenes/CertificadosPage';
import OrdenesImportacionPage from './pages/ordenes/OrdenesImportacionPage';
import OrdenesCamposConfigPage from './pages/ordenes/OrdenesCamposConfigPage';
import OrdenesNotificacionesPage from './pages/ordenes/OrdenesNotificacionesPage';
import TecnicoMobileView from './pages/ordenes/TecnicoMobileView';
import FourSatIntegrationPage from './pages/ordenes/FourSatIntegrationPage';

// Settings Pages
import ThemeConfigPage from './pages/settings/ThemeConfigPage';
import NotificationsConfigPage from './pages/settings/NotificationsConfigPage';

// Admin Pages
import AdminModulosPage from './pages/admin/AdminModulosPage';
import AdminEmpresasPage from './pages/admin/AdminEmpresasPage';

// Public pages (no auth required)
import CompletarOrdenClientePage from './pages/CompletarOrdenClientePage';

// Solicitudes de Permisos Module
import SolicitudesDashboard from './pages/solicitudes/SolicitudesDashboard';
import SolicitudesListPage from './pages/solicitudes/SolicitudesListPage';
import SolicitudDetallePage from './pages/solicitudes/SolicitudDetallePage';
import SolicitudFormPublico from './pages/solicitudes/SolicitudFormPublico';
import VerificacionPublicaPage from './pages/solicitudes/VerificacionPublicaPage';
import TiposSolicitudAdminPage from './pages/solicitudes/TiposSolicitudAdminPage';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
    const { isAuthenticated, user, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse">
                    <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // SuperAdmin can access everything
    if (user?.rol === 'superadmin') {
        return children;
    }

    if (allowedRoles.length > 0 && !allowedRoles.includes(user?.rol)) {
        return <Navigate to="/home" replace />;
    }

    return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading } = useAuth();

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse">
                    <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    if (isAuthenticated) {
        return <Navigate to="/home" replace />;
    }

    return children;
};

// QR Redirect Route (handles /qr/:id URLs)
const QRRedirectRoute = () => {
    const { isAuthenticated, user, loading } = useAuth();
    const docId = window.location.pathname.split('/qr/')[1];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-pulse">
                    <div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        // Store the QR URL to redirect after login
        sessionStorage.setItem('qr_redirect', docId);
        return <Navigate to="/login" replace />;
    }

    // If security, go to escaner with auto-load
    if (user?.rol === 'seguridad' || user?.rol === 'superadmin') {
        return <Navigate to={`/guias/documento/${docId}`} replace />;
    }

    // Others go to document detail
    return <Navigate to={`/guias/documento/${docId}`} replace />;
};

// Para rol seguridad/PI, su "área de inicio" es el punto de salida (escaneo + listado).
// Para los demás, va al DashboardPage estándar.
const GuiasDashboardRedirect = () => {
    const { user } = useAuth();
    if (user?.rol === 'seguridad') {
        return <Navigate to="/guias/todos" replace />;
    }
    return <DashboardPage />;
};

function AppRoutes() {
    return (
        <Routes>
            {/* Public Routes */}
            <Route
                path="/login"
                element={
                    <PublicRoute>
                        <LoginPage />
                    </PublicRoute>
                }
            />

            {/* QR Code redirect */}
            <Route path="/qr/:id" element={<QRRedirectRoute />} />

            {/* Public Route - Cliente completa orden (sin autenticación) */}
            <Route path="/completar-orden/:token" element={<CompletarOrdenClientePage />} />

            {/* Public Route - Registro Alarmas por link del admin */}
            <Route path="/alarmas/registro/:token" element={<AlarmasRegistroPublicoPage />} />

            {/* Public Routes - Solicitudes de Permisos (sin autenticación) */}
            <Route path="/solicitudes/publico/:slug" element={<SolicitudFormPublico />} />
            <Route path="/verificar/:slug/:id" element={<VerificacionPublicaPage />} />

            {/* Home - Module Selection */}
            <Route
                path="/home"
                element={
                    <ProtectedRoute>
                        <HomePage />
                    </ProtectedRoute>
                }
            />

            {/* SuperAdmin Routes */}
            <Route
                path="/superadmin"
                element={
                    <ProtectedRoute allowedRoles={['superadmin']}>
                        <SuperAdminPage />
                    </ProtectedRoute>
                }
            />

            {/* Gestión de Usuarios */}
            <Route
                path="/gestion-usuarios"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <GestionUsuariosPage />
                    </ProtectedRoute>
                }
            />

            {/* Guías de Despacho Module Routes */}
            <Route
                path="/guias/dashboard"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'solicitante', 'gerente', 'seguridad', 'superadmin']}>
                        <GuiasDashboardRedirect />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/documento/:id"
                element={
                    <ProtectedRoute>
                        <DocumentoDetallePage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/crear"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'solicitante']}>
                        <CrearGuiaPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/mis-guias"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'solicitante']}>
                        <MisGuiasPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/aprobar"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'gerente']}>
                        <AprobarGuiasPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/punto-salida"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'seguridad']}>
                        <PuntoSalidaPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/escaner"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'seguridad']}>
                        <EscanerQRPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/todos"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'gerente', 'seguridad', 'superadmin']}>
                        <DocumentosPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/reportes"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'gerente', 'seguridad', 'superadmin']}>
                        <ReportesGuiasPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/usuarios"
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <UsuariosPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/carga-masiva"
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <CargaMasivaPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/configuracion"
                element={
                    <ProtectedRoute allowedRoles={['admin']}>
                        <ConfiguracionPage />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/guias/historial"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'gerente']}>
                        <HistorialPage />
                    </ProtectedRoute>
                }
            />

            {/* Legacy routes - redirect to new structure */}
            <Route path="/dashboard" element={<Navigate to="/guias/dashboard" replace />} />
            <Route path="/crear-guia" element={<Navigate to="/guias/crear" replace />} />
            <Route path="/mis-guias" element={<Navigate to="/guias/mis-guias" replace />} />
            <Route path="/aprobar" element={<Navigate to="/guias/aprobar" replace />} />
            <Route path="/punto-salida" element={<Navigate to="/guias/punto-salida" replace />} />
            <Route path="/documentos" element={<Navigate to="/guias/todos" replace />} />
            <Route path="/documento/:id" element={<Navigate to="/guias/documento/:id" replace />} />
            <Route path="/usuarios" element={<Navigate to="/guias/usuarios" replace />} />
            <Route path="/carga-masiva" element={<Navigate to="/guias/carga-masiva" replace />} />
            <Route path="/configuracion" element={<Navigate to="/guias/configuracion" replace />} />
            <Route path="/historial" element={<Navigate to="/guias/historial" replace />} />

            {/* ==================== ADMIN MODULE MANAGEMENT ==================== */}
            <Route
                path="/admin/modulos"
                element={
                    <ProtectedRoute allowedRoles={['superadmin']}>
                        <AdminModulosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/admin/empresas"
                element={
                    <ProtectedRoute allowedRoles={['superadmin']}>
                        <AdminEmpresasPage />
                    </ProtectedRoute>
                }
            />

            {/* Órdenes de Servicio Module */}
            <Route
                path="/ordenes/dashboard"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesDashboardPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/clientes"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesClientesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/clientes/:clienteId"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <ClienteDetallePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/ordenes"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesListPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/nueva"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenFormPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/ordenes/:ordenId"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenDetallePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/inventario"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesInventarioPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/vehiculos"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesVehiculosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/vehiculos/:vehiculoId"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <VehiculoDetallePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/dispositivos"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesDispositivosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/dispositivos-instalados"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <DispositivosInstaladosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/configuracion"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesConfigPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/calendario"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesCalendarioPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/reportes"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesReportesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/reportes/constructor"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <ReportesCustomPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/certificados"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin', 'tecnico']}>
                        <CertificadosPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/importacion"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesImportacionPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/configuracion/campos"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesCamposConfigPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/configuracion/notificaciones"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <OrdenesNotificacionesPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/tecnico"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin', 'tecnico', 'supervisor']}>
                        <TecnicoMobileView />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/ordenes/integraciones/4sat"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <FourSatIntegrationPage />
                    </ProtectedRoute>
                }
            />

            {/* Settings Routes */}
            <Route
                path="/configuracion/tema"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <ThemeConfigPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/configuracion/notificaciones"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin', 'tecnico', 'supervisor']}>
                        <NotificationsConfigPage />
                    </ProtectedRoute>
                }
            />

            {/* ============ MÓDULO SOLICITUDES DE PERMISOS ============ */}
            {/* Rutas protegidas PI */}
            <Route
                path="/solicitudes/dashboard"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'seguridad', 'superadmin']}>
                        <SolicitudesDashboard />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/solicitudes/lista"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'seguridad', 'superadmin']}>
                        <SolicitudesListPage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/solicitudes/detalle/:id"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'seguridad', 'superadmin']}>
                        <SolicitudDetallePage />
                    </ProtectedRoute>
                }
            />
            <Route
                path="/solicitudes/admin/tipos"
                element={
                    <ProtectedRoute allowedRoles={['admin', 'superadmin']}>
                        <TiposSolicitudAdminPage />
                    </ProtectedRoute>
                }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/home" replace />} />

            {/* ============ MÓDULO ALARMAS_LS ============ */}
            <Route
                path="/alarmas/*"
                element={
                    <ProtectedRoute>
                        <Routes>
                            <Route path="panel" element={<AlarmasPanelPage />} />
                            <Route path="admin" element={<AlarmasAdminPage />} />
                            <Route path="historial" element={<AlarmasHistorialPage />} />
                            <Route path="manual" element={<AlarmasManualPage />} />
                            <Route path="*" element={<Navigate to="/alarmas/panel" replace />} />
                        </Routes>
                    </ProtectedRoute>
                }
            />

            {/* Integrador Codelco-Samtech */}
            <Route
                path="/codelco/*"
                element={
                    <ProtectedRoute>
                        <Routes>
                            <Route path="dashboard" element={<CodelcoDashboardPage />} />
                            <Route path="vehiculos" element={<CodelcoVehiculosPage />} />
                            <Route path="perfiles" element={<CodelcoPerfilesPage />} />
                            <Route path="mapeo" element={<CodelcoMapeoPage />} />
                            <Route path="bitacora" element={<CodelcoBitacoraPage />} />
                            <Route path="notificaciones" element={<CodelcoNotificacionesPage />} />
                            <Route path="config" element={<CodelcoConfigPage />} />
                            <Route path="*" element={<Navigate to="/codelco/dashboard" replace />} />
                        </Routes>
                    </ProtectedRoute>
                }
            />
        </Routes>
    );
}

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <BrowserRouter>
                    <AppRoutes />
                    <Toaster 
                        position="top-right" 
                        richColors 
                        closeButton
                        toastOptions={{
                            className: 'font-sans',
                        }}
                    />
                </BrowserRouter>
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
